import React, { useEffect, FC, useState } from 'react'
import { container } from 'src/inversify.config'
import { withTranslation, WithTranslation } from 'react-i18next'
import { withIdentity, IdentityProps } from 'src/core/services/authentication'
import { TenantSummary, TenantSummaryStore } from 'src/stores/tenants/tenants-store'
import { TableModel, TableView, getDefaultTableSettings } from 'src/core/ui/collections/table'
import { Row, Card, Col, Tag, Select, Tooltip, Button, Checkbox, Drawer } from 'antd'
import { formatMessage } from 'src/core/services/http.service'
import { Query } from 'src/core/stores/data-store'
import { withRouter, RouteComponentProps, Link } from 'react-router-dom'
import ContentHeader from 'src/core/ui/content-header'
import NewEntityDialog from './tenant-new'
import EntityHierarchyPath from './components/tree-section/tenant-hierarchy-path'
import { IdentityType } from 'src/stores/tenants/common'
import { ApartmentOutlined, DeploymentUnitOutlined, DownloadOutlined, GoldOutlined, ReloadOutlined, UserOutlined } from '@ant-design/icons'
import AreaSelect from 'src/components/areaEdit'
import { CacheProps, withCache } from 'src/core/services/cache.service'
import { successNotification } from '../../../components/systemNotification/notificationService'
import { TenantDetailsHierarchyTreeSection } from './components/tree-section/tenant-details-hierarchy'

const { Option } = Select

interface EntityListPageProps extends WithTranslation, IdentityProps, CacheProps, RouteComponentProps { }

const EntityListPage: FC<EntityListPageProps> = (props) => {
  const [IsAdmin] = useState(props.identity.roles?.any(r => r === "Administrator"))
  const [IsUser] = useState(props.identity.roles?.any(r => r === "User"))
  const [IsManager] = useState(props.identity.roles?.any(r => r === "Manager"))
  const [areaFilter, setAreaFilter] = useState<any>()
  const [creationFilter, setCreationFilter] = useState<any>()
  const [initPagination, setInitPagination] = useState(false)
  const [query, setQuery] = useState({
    searchQuery: '',
    orderBy: [{ field: 'title', direction: 'Ascending', useProfile: false }],
    skip: 0,
    take: getDefaultTableSettings().defaultPageSize,
    parameters: {
      includeHierarchy: 'true',
    },
    odataObject: {
      filter: {}
    },
  } as Query)

  const cacheKey = "IDENTITY-TENANTS-LIST"
  const [newEntityVisibility, setNewEntityVisibility] = useState(false)

  const [indeterminate, setIndeterminate] = useState(0)
  const [showHierarchy, setShowHierarchy] = useState(false)
  const [selectedNode, setSelectedNode] = useState<any>()


  const entitiesStore = container.get<TenantSummaryStore>(TenantSummaryStore)
  const entityState = entitiesStore.state
  const t = props.t

  const getisUserCustomizedValue = (value) => {
    if (value % 3 == 0) return undefined
    if (value % 3 == 1) return true
    else return false
  }
  useEffect(() => {
    var queryStored = props.cache.getWithCustomKey(cacheKey)
    if (queryStored && queryStored.query)
      setQuery(queryStored.query)
    if (queryStored && queryStored.area)
      setAreaFilter(queryStored.area)
    if (queryStored && queryStored.type)
      setCreationFilter(queryStored.type)
    if (queryStored && queryStored.isUserCustomized) {
      var current = 0
      if (queryStored.isUserCustomized % 3 == 0)
        setIndeterminate(0)
      else
        if (queryStored.isUserCustomized % 3 == 1)
          setIndeterminate(1)
        else setIndeterminate(2)

    }
    entitiesStore.load(Build(queryStored?.query || query, queryStored?.area || areaFilter, (queryStored?.type) || creationFilter, queryStored?.isUserCustomized || getisUserCustomizedValue(indeterminate)))
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const Build = (query: Query, area: any, type: any, isUserCustomized: any): Query => {
    if (area) {
      var filter = {
        areaId: { eq: { type: 'guid', value: area.id } }
      }
      query.odataObject.filter.area = filter
    }
    else {
      query.odataObject.filter.area = undefined
    }
    if (isUserCustomized) {
      var cfilter = {
        isUserCustomized: { eq: isUserCustomized }
      }
      query.odataObject.filter.isUserCustomized = cfilter
    }
    else {
      query.odataObject.filter.isUserCustomized = undefined
    }
    if (type) {
      query.parameters.creation = type
    }
    else {
      query.parameters.creation = undefined
    }
    props.cache.saveWithCustomKey(cacheKey, { query: query, area: area, type: type, isUserCustomized: isUserCustomized })
    return query
  }

  const onAreaFilterChange = (area: any) => {
    setInitPagination(!initPagination)
    setAreaFilter(area)
    var newquery = query
    newquery.skip = 0
    setQuery(newquery)
    entitiesStore.load(Build(newquery, area, creationFilter, getisUserCustomizedValue(indeterminate)))
  }

  const onCreationFilterChange = (type: any) => {
    setInitPagination(!initPagination)
    setCreationFilter(type)
    var newquery = query
    newquery.skip = 0
    setQuery(newquery)
    entitiesStore.load(Build(newquery, areaFilter, type, getisUserCustomizedValue(indeterminate)))
  }

  const onDownloadExcel = async () => {
    await entitiesStore.export(Build(query, areaFilter, creationFilter, getisUserCustomizedValue(indeterminate)))
    successNotification(t('Operation Completed Successfully'), t('Your operation will be processed in background.'))
  }
  const onChangeisUserCustomized = (value) => {
    var newquery = query
    entitiesStore.load(Build(newquery, areaFilter, creationFilter, getisUserCustomizedValue(indeterminate + 1)))
    setIndeterminate(indeterminate + 1)
  }

  const tableModel = {
    query: query,
    columns: [
      {
        sortable: true,
        searchable: true,
        field: 'title',
        title: t('Title'),
        fixed: 'left',
        width: "500px",
        renderer: (data: TenantSummary) => (
          <span>
            <Tooltip title={t("Show hierarchy")}><Button style={{ marginRight: 10 }} onClick={() => { setSelectedNode(data.id); setShowHierarchy(true) }} type="primary" icon={< ApartmentOutlined />} /> </Tooltip>
            <Link to={{ pathname: `/admin/tenants/${data.id}`, state: { entityId: data.id, entityTitle: data.title } }}>
              <span style={{ fontSize: 10 }}>{!data.title || data.title.length == 0 ? t("(Empty Name)") : data.title}</span>
            </Link>

          </span>
        ),
      },
      {
        searchable: true,
        width: "200px",
        field: 'groupAlias',
        title: t('Group Alias'),
        renderer: (data: TenantSummary) => <span style={{ fontSize: "10px", fontWeight: "bold", cursor: "text" }}>{data.groupAlias}</span>
      },
      {
        searchable: true,
        field: 'taxNumber',
        title: t('CIF'),
        renderer: (data: TenantSummary) => <span style={{ fontSize: "10px", fontWeight: "bold", cursor: "text" }}>{data.taxNumber}</span>
      },
      {
        field: 'area',
        title: t('Area'),
        renderer: (data: TenantSummary) => <span style={{ fontSize: "10px", fontWeight: "bold", cursor: "text" }}>{data.areaName}</span>
      },
      {
        field: 'auto',
        title: t('Creation'),
        renderer: (data: TenantSummary) => <span style={{ fontSize: "10px", fontWeight: "bold", cursor: "text" }}>
          {data.auto ? <Tag icon={<ReloadOutlined />} color="#87d068">
            {t("Auto")}
          </Tag> :
            <Tag icon={<UserOutlined />} color="#108ee9">
              {t("Manual")}
            </Tag>
          }</span>
      },

    ],
    data: entityState.value,
    sortFields: [],
  } as TableModel<TenantSummary>

  var rightToolbar = (
    <React.Fragment>
      <li><Tooltip placement="topLeft" title={t('Export to Excel')}><Button onClick={e => onDownloadExcel()} >
        <DownloadOutlined />
      </Button></Tooltip></li>
      <li>
        <Checkbox style={{ marginTop: 5, marginLeft: 15 }} indeterminate={indeterminate % 3 == 0} onChange={(x) => onChangeisUserCustomized(x.target.checked)} checked={indeterminate % 3 == 1} >
          {t("Customized?")}
        </Checkbox>
      </li>
      <li>
        <Select defaultValue={creationFilter} allowClear placeholder={t("Select Creation type...")} style={{ width: 250 }} onChange={(value) => onCreationFilterChange(value)}>
          <Option value="true">{
            <Tag icon={<ReloadOutlined />} color="#87d068">
              {t("Auto")}
            </Tag>}</Option>
          <Option value="false">{
            <Tag icon={<UserOutlined />} color="#108ee9">
              {t("Manual")}
            </Tag>}</Option>
        </Select>
      </li>
      <li>
        <AreaSelect minWidth={200} value={areaFilter} placeholder={t("Select Area...")} valueAsItemReference onChange={(value) => onAreaFilterChange(value)} />
      </li>

    </React.Fragment>
  )

  return (
    <div>
      <Row gutter={0}>
        <Col span={16}>
          <ContentHeader title={t('Tenants')} />
        </Col>
        <Col span={8} />
      </Row>
      <Row align="top" justify="space-between">
        <Card style={{ width: '100%' }}>
          <TableView
            rowKey="id"
            enableTableSettings
            rightToolbar={rightToolbar}
            showDeleteStatusWindow
            scroll={{ x: 1800 }}
            initPagination={initPagination}
            canCreateNew={IsAdmin || IsManager}
            canDelete={IsAdmin || IsManager}
            onDeleteRow={(item: any) => entitiesStore.delete(item.id)}
            onNewItem={() => setNewEntityVisibility(true)}
            onQueryChanged={(query: Query) => {
              setQuery(query)
              entitiesStore.load(Build(query, areaFilter, creationFilter, getisUserCustomizedValue(indeterminate)))
            }}
            onRefresh={() => entitiesStore.load(query)}
            model={tableModel}
            error={entityState.errorMessage.value && formatMessage(entityState.errorMessage.value)}
          />
          {newEntityVisibility && (
            <NewEntityDialog
              onClose={(response) => {
                setNewEntityVisibility(false)
                if (response) {
                  props.history.push(`/admin/tenants/${response.identity}`, { entityId: response.identity, entityTitle: response.title })
                }
              }}
            />
          )}
        </Card>
      </Row>
      <Drawer width={800} title={t("Hierarchy")} placement="right" onClose={() => setShowHierarchy(false)} visible={showHierarchy}>
        <TenantDetailsHierarchyTreeSection
          forceReload={0}
          entityId={selectedNode}
          {...props}
          onSelectionChange={(e) => {

          }}
        />
      </Drawer>
    </div>
  )
}

export default withCache(withIdentity(withTranslation()(withRouter(EntityListPage))))
